/**
 * ホーム画面（§7.1）：今日のS_act/MED比、塗布タップ（部位・量3択）、水泳申告、
 * 外出モード開始/終了、屋外予定（SCHED用）登録。
 */
import {
  applySwim,
  APPLY_AMOUNT_R,
  DEGRADED_MESSAGE,
  describeSunburn,
  MED_SED,
  minutesToWarn,
  POOL_UNIT_NOTE,
  recordApplication,
  sWarn,
} from "@uvk/shared";
import type { AlertContext, ApplyAmount, Application } from "@uvk/shared";
import { adherenceSpfLabel, jstHour, notify, state } from "../state.js";
import { startSession, stopSession } from "../session.js";
import { saveApplication } from "../db.js";
import { createSchedule } from "../api.js";
import { esc } from "../ui.js";

let selectedAmount: ApplyAmount = "normal";
let selectedParts = new Set<string>(["顔"]);

const PARTS = ["顔", "腕", "首", "脚"];

export function renderHome(el: HTMLElement): void {
  const e = state.engine;
  const med = MED_SED[state.settings.skinType];
  const warn = sWarn(state.settings.skinType);
  const pct = Math.min(100, (e.sAct / med) * 100);

  const ctx: Pick<AlertContext, "sedRatePerS" | "settings"> = {
    sedRatePerS: (state.lastDetail?.sedRatePerH ?? 0) / 3600,
    settings: state.settings,
  };
  const mins = minutesToWarn(e, {
    ...ctx,
    now: Date.now(),
    visible: state.lastDetail?.visible ?? false,
    indoor: state.lastDetail?.indoor ?? true,
    wx: { uvi: 0, uviClearSky: 0, fdBb: 0, cmf: 0, tempC: 0, rh: 0, pm25: 0, degraded: false },
    hourJst: jstHour(Date.now()),
  });
  const minsText =
    !state.sessionActive ? "—"
    : mins === Infinity ? "蓄積停止中"
    : mins <= 0 ? "いま塗りどき"
    : `あと${Math.round(mins)}分`;

  const product = state.products[0];
  const burn = describeSunburn(e.sAct);

  el.innerHTML = `
    ${state.degraded ? `<div class="alert-item">${DEGRADED_MESSAGE}</div>` : ""}
    <div class="card">
      <h2>今日の紫外線 <span class="sub">スキンタイプ${["I", "II", "III", "IV"][state.settings.skinType - 1]}（MED ${med} SED）</span></h2>
      <div class="row">
        <span class="big">${burn.poolTime.replace("ぶん", "")}</span>
        <span class="sub grow">${burn.p}</span>
        <span class="pill ${state.lastDetail?.visible ? "sun" : "shade"}">
          ${!state.sessionActive ? "計測停止中" : state.lastDetail?.indoor ? "屋内" : state.lastDetail?.visible ? "日向" : "日陰"}
        </span>
      </div>
      <div class="meter"><div style="width:${pct}%"></div></div>
      <p class="sub">${burn.sed} ／ 肌の限界(MED)の ${Math.round(pct)}%</p>
      <p class="sub">塗りどきまで：<b>${minsText}</b>（警告閾値 ${warn.toFixed(1)} SED）</p>
      <button id="session-btn" class="primary" style="width:100%">
        ${state.sessionActive ? "⏹ 外出モードを終了" : "▶ 外出モードを開始"}
      </button>
      <p class="sub">屋外 ${e.minOutdoor}分・日向 ${e.minSun}分${e.minUnknown > 0 ? `・計測できなかった時間 ${e.minUnknown}分` : ""}</p>
      <p class="sub">${POOL_UNIT_NOTE}</p>
    </div>

    <div class="card">
      <h2>🧴 塗った！</h2>
      ${product ? `<p class="sub">${product.spf === 50 && product.spfPlus ? "SPF50+" : `SPF${product.spf}`} / PA${"+".repeat(product.pa)}${product.waterproof ? " / WP" : ""}</p>` : `<p class="sub">先に「商品」タブで日焼け止めを登録してください</p>`}
      <div class="row" id="parts-row">
        ${PARTS.map((p) => `<button class="ghost ${selectedParts.has(p) ? "selected" : ""}" data-part="${p}">${p}</button>`).join("")}
      </div>
      <div class="row" style="margin-top:8px" id="amount-row">
        ${(Object.keys(APPLY_AMOUNT_R) as ApplyAmount[])
          .map((a) => {
            const label = { full: "たっぷり", normal: "ふつう", thin: "うすく" }[a];
            return `<button class="ghost ${selectedAmount === a ? "selected" : ""}" data-amount="${a}">${label}</button>`;
          })
          .join("")}
      </div>
      <div class="row" style="margin-top:10px">
        <button id="apply-btn" class="primary grow" ${product ? "" : "disabled"}>塗布を記録</button>
        <button id="swim-btn" class="ghost">🏊 泳いだ</button>
      </div>
    </div>

    <div class="card">
      <h2>📅 屋外の予定（10分前にお知らせ）</h2>
      <div class="row">
        <input type="datetime-local" id="sched-time" class="grow" />
        <input type="text" id="sched-label" placeholder="公園" style="width:90px" />
        <button id="sched-btn" class="ghost">登録</button>
      </div>
    </div>

    <div class="card">
      <h2>🔔 アラート</h2>
      ${state.alertsLog.length === 0 ? `<p class="sub">まだありません</p>` : state.alertsLog.slice(0, 6).map((a) => `<div class="alert-item">${esc(a.message)}</div>`).join("")}
    </div>
  `;

  el.querySelector<HTMLButtonElement>("#session-btn")!.onclick = () => {
    void (state.sessionActive ? stopSession() : startSession());
  };

  el.querySelectorAll<HTMLButtonElement>("#parts-row button").forEach((b) => {
    b.onclick = () => {
      const p = b.dataset.part!;
      selectedParts.has(p) ? selectedParts.delete(p) : selectedParts.add(p);
      notify();
    };
  });
  el.querySelectorAll<HTMLButtonElement>("#amount-row button").forEach((b) => {
    b.onclick = () => {
      selectedAmount = b.dataset.amount as ApplyAmount;
      notify();
    };
  });

  el.querySelector<HTMLButtonElement>("#apply-btn")!.onclick = () => {
    if (!product) return;
    const app: Application = {
      ts: Date.now(),
      productId: product.id,
      spfLabel: product.spf,
      amount: selectedAmount,
      parts: [...selectedParts],
      waterproof: product.waterproof,
      swimFactor: 1,
    };
    recordApplication(state.engine, app);
    void saveApplication(app);
    notify();
  };

  el.querySelector<HTMLButtonElement>("#swim-btn")!.onclick = () => {
    if (state.engine.application) {
      state.engine.application = applySwim(state.engine.application);
      notify();
    }
  };

  el.querySelector<HTMLButtonElement>("#sched-btn")!.onclick = async () => {
    const t = el.querySelector<HTMLInputElement>("#sched-time")!.value;
    const label = el.querySelector<HTMLInputElement>("#sched-label")!.value || "外出";
    if (!t) return;
    const ok = await createSchedule(new Date(t).getTime(), label);
    alert(ok ? "予定を登録しました。10分前にお知らせします" : "登録できませんでした（オフライン？）");
  };
}
